import React, { useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Button, Grid, Paper, Chip, Stack, Divider,
  Accordion, AccordionSummary, AccordionDetails, Container, Modal, IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import services from '../data/services';

export default function ServicePage() {
  const { id } = useParams<{ id: string }>();
  const service = services.find((s) => s.id === id);
  const [expandedFaq, setExpandedFaq] = useState<number | false>(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const gallery = service?.gallery ?? [];
  const shorts = service?.shorts ?? [];

  if (!service) {
    return (
      <Container sx={{ py: 16, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ mb: 3 }}>Service not found</Typography>
        <Button component={RouterLink} to="/#services" startIcon={<ArrowBackIcon />} variant="outlined">
          Back to Services
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ background: '#0d0a12', minHeight: '100vh', pt: 10 }}>
      {/* Hero */}
      <Box sx={{
        py: { xs: 8, md: 12 },
        px: { xs: 3, md: 8 },
        background: `radial-gradient(ellipse 70% 50% at 60% 0%, ${service.color}18 0%, transparent 70%), #0d0a12`,
        borderBottom: '1px solid rgba(255,110,180,0.1)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: `linear-gradient(${service.color}06 1px, transparent 1px), linear-gradient(90deg, ${service.color}06 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
          maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black, transparent)',
        }} />
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Button
            component={RouterLink}
            to="/#services"
            startIcon={<ArrowBackIcon />}
            sx={{ color: 'text.secondary', mb: 4, '&:hover': { color: 'primary.main' } }}
          >
            All Services
          </Button>

          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '3.5rem', filter: `drop-shadow(0 0 20px ${service.color}88)` }}>
              {service.emoji}
            </Typography>
            <Box>
              <Typography variant="overline" sx={{ color: service.color, letterSpacing: 4 }}>
                SERVICE
              </Typography>
              <Typography variant="h1" sx={{ fontSize: { xs: '2.5rem', md: '4rem' }, lineHeight: 1.1 }}>
                {service.title}
              </Typography>
            </Box>
          </Stack>

          <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 400, mb: 4, maxWidth: 600 }}>
            {service.subtitle}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1, mb: 5 }}>
            {service.tags.map((tag) => (
              <Chip key={tag} label={tag} size="small" sx={{
                background: `${service.color}18`, border: `1px solid ${service.color}44`, color: service.color,
              }} />
            ))}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="contained"
              size="large"
              href="https://discord.com/users/panekopanko"
              sx={{
                background: `linear-gradient(135deg, ${service.color}, ${service.color}cc)`,
                px: 4, py: 1.5,
                '&:hover': { boxShadow: `0 0 30px ${service.color}55` },
              }}
            >
              DM me on Discord
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: `${service.color}55`, color: service.color, px: 4, py: 1.5,
                '&:hover': { borderColor: service.color, background: `${service.color}10` },
              }}
            >
              View Portfolio
            </Button>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        {/* About the service */}
        <Grid container spacing={8} sx={{ mb: 10 }}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Typography variant="overline" sx={{ color: service.color, letterSpacing: 4 }}>
              OVERVIEW
            </Typography>
            <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, mt: 1, mb: 4 }}>
              What's Included
            </Typography>
            {service.longDesc.map((para, i) => (
              <Typography key={i} variant="body1" sx={{ color: 'text.secondary', lineHeight: 2, mb: 3, fontSize: '1.05rem' }}>
                {para}
              </Typography>
            ))}
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper sx={{
              p: 4, background: `${service.color}08`,
              border: `1px solid ${service.color}25`,
            }}>
              <Typography variant="h6" sx={{ mb: 3, color: service.color }}>Starting Price</Typography>
              <Typography variant="h2" sx={{ mb: 1, fontSize: '3rem', fontWeight: 700 }}>{service.price}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
                Final price depends on scope. DM me on Discord to discuss.
              </Typography>
              <Divider sx={{ borderColor: `${service.color}20`, mb: 3 }} />
              <Stack spacing={1.5}>
                {['Free discovery call', 'Source files included', 'Revision rounds included', 'Aftercare support'].map((item) => (
                  <Stack key={item} direction="row" spacing={1.5} alignItems="center">
                    <CheckCircleOutlineIcon sx={{ color: service.color, fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Button
                fullWidth
                variant="contained"
                href="https://discord.com/users/panekopanko"
                sx={{
                  mt: 4,
                  background: `linear-gradient(135deg, ${service.color}, ${service.color}cc)`,
                  '&:hover': { boxShadow: `0 0 20px ${service.color}44` },
                }}
              >
                DM me on Discord
              </Button>
            </Paper>
          </Grid>
        </Grid>

        {/* Process */}
        <Box sx={{ mb: 12 }}>
          <Typography variant="overline" sx={{ color: service.color, letterSpacing: 4 }}>
            PROCESS
          </Typography>
          <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, mt: 1, mb: 6 }}>
            How It Works
          </Typography>
          <Grid container spacing={3}>
            {service.process.map((step, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={step.step}>
                <Paper sx={{
                  p: 3.5, height: '100%',
                  background: 'rgba(22,15,32,0.9)',
                  border: `1px solid ${service.color}15`,
                  transition: 'all 0.3s',
                  '&:hover': { borderColor: `${service.color}55`, transform: 'translateY(-4px)' },
                }}>
                  <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: `${service.color}44`, mb: 1 }}>
                    {step.step}
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1rem' }}>{step.title}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>{step.desc}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Tracking & Physics explainers — 3D modelling only */}
        {service.id === '3d-modelling' && (
          <Box sx={{ mb: 12 }}>
            <Typography variant="overline" sx={{ color: service.color, letterSpacing: 4 }}>
              TECH
            </Typography>
            <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, mt: 1, mb: 6 }}>
              What's Under the Hood
            </Typography>
            <Stack spacing={4}>

              {/* Tracking */}
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Facial Tracking</Typography>
                <Typography sx={{ color: 'text.secondary', mb: 3, fontSize: '0.95rem', lineHeight: 1.8 }}>
                  VRM blendshape tracking is included in every model. PerfectSync ARKit is a separate, more advanced system that requires an iPhone and is evaluated per commission.
                </Typography>
                <Grid container spacing={3}>
                  {[
                    {
                      label: 'VRM Blendshape Tracking',
                      tag: 'Included by default',
                      color: service.color,
                      points: [
                        'Works with any webcam or phone camera using face-tracking software like VTube Studio.',
                        'Drives a set of predefined VRM expressions — blink, happy, angry, sad, surprised — which your software maps to your face movements in real time.',
                        'Simple, robust, and compatible with every VRM-based application: VTube Studio, Warudo, VRChat, and more.',
                        'No extra hardware needed. This is the standard tracking system for the vast majority of VTubers.',
                      ],
                    },
                    {
                      label: 'PerfectSync ARKit',
                      tag: 'Evaluated per model',
                      color: '#b44fff',
                      points: [
                        'Uses the TrueDepth camera on an iPhone with Face ID to track 52 individual ARKit blendshapes.',
                        'Where VRM tracking drives broad preset expressions, ARKit captures the raw movement of your face directly — eyebrows, cheek puffs, jaw slide, lip curl, tongue, squints, and more — all independently.',
                        'The result is significantly more nuanced and expressive animation that mirrors your real face movements rather than approximating them.',
                        'Not included by default — the 52 blendshapes must be manually authored and weighted for each model, and whether that\'s feasible depends on the character\'s facial structure. This is scoped during the commission process.',
                      ],
                    },
                  ].map((item) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={item.label}>
                      <Paper sx={{
                        p: 3, height: '100%',
                        background: `${item.color}08`,
                        border: `1px solid ${item.color}30`,
                        borderRadius: 3,
                      }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                          <Chip label={item.tag} size="small" sx={{
                            background: `${item.color}22`, border: `1px solid ${item.color}55`,
                            color: item.color, fontWeight: 700, fontSize: '0.7rem',
                          }} />
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>{item.label}</Typography>
                        </Stack>
                        <Stack spacing={1.2}>
                          {item.points.map((p, i) => (
                            <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                              <Box sx={{ mt: '7px', width: 5, height: 5, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
                              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>{p}</Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Divider sx={{ borderColor: `${service.color}15` }} />

              {/* Physics */}
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Cloth & Hair Physics</Typography>
                <Typography sx={{ color: 'text.secondary', mb: 3, fontSize: '0.95rem', lineHeight: 1.8 }}>
                  Models ship with both physics systems — Springbones for cross-platform use, MagicaCloth 2 for Warudo.
                </Typography>
                <Grid container spacing={3}>
                  {[
                    {
                      label: 'VRM Springbones',
                      tag: 'All platforms',
                      color: service.color,
                      points: [
                        'Built into the VRM spec — works everywhere VRM does.',
                        'Chain-based simulation for hair, ears, tails, and light accessories.',
                        'Lightweight and broadly compatible across all VRM apps.',
                        'Best for: cross-platform use and VRChat.',
                      ],
                    },
                    {
                      label: 'MagicaCloth 2',
                      tag: 'Warudo only',
                      color: '#b44fff',
                      points: [
                        'Advanced mesh-based cloth simulation, exclusive to Warudo.',
                        'Handles complex fabric — skirts, capes, scarves, ribbons — with realistic draping.',
                        'Dramatically reduces clipping on layered or flowing outfits.',
                        'Best for: Warudo users with complex clothing.',
                      ],
                    },
                  ].map((item) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={item.label}>
                      <Paper sx={{
                        p: 3, height: '100%',
                        background: `${item.color}08`,
                        border: `1px solid ${item.color}30`,
                        borderRadius: 3,
                      }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                          <Chip label={item.tag} size="small" sx={{
                            background: `${item.color}22`, border: `1px solid ${item.color}55`,
                            color: item.color, fontWeight: 700, fontSize: '0.7rem',
                          }} />
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>{item.label}</Typography>
                        </Stack>
                        <Stack spacing={1.2}>
                          {item.points.map((p, i) => (
                            <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                              <Box sx={{ mt: '7px', width: 5, height: 5, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
                              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>{p}</Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>

            </Stack>
          </Box>
        )}

        {/* Shorts + Gallery — 3D modelling only */}
        {service.id === '3d-modelling' && (shorts.length > 0 || gallery.length > 0) && (
          <Box sx={{ mb: 12 }}>
            <Typography variant="overline" sx={{ color: service.color, letterSpacing: 4 }}>
              LOW POLY
            </Typography>
            <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, mt: 1, mb: 6 }}>
              See It in Action
            </Typography>

            {/* YouTube Shorts */}
            {shorts.length > 0 && (
              <Grid container spacing={3} sx={{ mb: gallery.length > 0 ? 6 : 0 }}>
                {shorts.map((videoId) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={videoId}>
                    <Box sx={{
                      borderRadius: 3, overflow: 'hidden',
                      border: `1px solid ${service.color}25`,
                      boxShadow: `0 0 40px ${service.color}12`,
                      position: 'relative', paddingTop: '56.25%',
                    }}>
                      <Box
                        component="iframe"
                        src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`}
                        title={`Short ${videoId}`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        sx={{
                          position: 'absolute', inset: 0,
                          width: '100%', height: '100%', border: 'none',
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Image gallery */}
            {gallery.length > 0 && (
              <>
                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 3, fontSize: '0.7rem', display: 'block', mb: 3 }}>
                  GALLERY
                </Typography>
                <Grid container spacing={2}>
                  {gallery.map((img, i) => (
                    <Grid size={{ xs: 6, sm: 4, md: 3 }} key={i}>
                      <Box
                        onClick={() => setLightboxIndex(i)}
                        sx={{
                          borderRadius: 2, overflow: 'hidden', cursor: 'pointer',
                          aspectRatio: '1/1',
                          border: `1px solid ${service.color}20`,
                          transition: 'all 0.25s',
                          '&:hover': { borderColor: service.color, transform: 'scale(1.02)', boxShadow: `0 8px 24px ${service.color}22` },
                        }}
                      >
                        <Box
                          component="img"
                          src={img.src}
                          alt={img.caption ?? `Gallery ${i + 1}`}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Lightbox */}
                <Modal open={lightboxIndex !== null} onClose={() => setLightboxIndex(null)}>
                  <Box sx={{
                    position: 'fixed', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(13,10,18,0.95)',
                  }}>
                    <IconButton onClick={() => setLightboxIndex(null)} sx={{ position: 'absolute', top: 16, right: 16, color: 'text.secondary' }}>
                      <CloseIcon />
                    </IconButton>
                    {lightboxIndex !== null && lightboxIndex > 0 && (
                      <IconButton onClick={() => setLightboxIndex(lightboxIndex - 1)} sx={{ position: 'absolute', left: 16, color: 'text.primary' }}>
                        <ChevronLeftIcon sx={{ fontSize: 36 }} />
                      </IconButton>
                    )}
                    {lightboxIndex !== null && (
                      <Box sx={{ maxWidth: '90vw', maxHeight: '90vh', textAlign: 'center' }}>
                        <Box
                          component="img"
                          src={gallery[lightboxIndex].src}
                          alt={gallery[lightboxIndex].caption}
                          sx={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 2, display: 'block', mx: 'auto' }}
                        />
                        {gallery[lightboxIndex].caption && (
                          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
                            {gallery[lightboxIndex].caption}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {lightboxIndex !== null && lightboxIndex < gallery.length - 1 && (
                      <IconButton onClick={() => setLightboxIndex(lightboxIndex + 1)} sx={{ position: 'absolute', right: 16, color: 'text.primary' }}>
                        <ChevronRightIcon sx={{ fontSize: 36 }} />
                      </IconButton>
                    )}
                  </Box>
                </Modal>
              </>
            )}
          </Box>
        )}

        {/* Pricing */}
        <Box sx={{ mb: 12 }}>
          <Typography variant="overline" sx={{ color: service.color, letterSpacing: 4 }}>
            PRICING
          </Typography>
          <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, mt: 1, mb: 6 }}>
            Packages
          </Typography>
          <Grid container spacing={3} alignItems="stretch">
            {service.pricingTiers.map((tier, tierIndex) => (
              <Grid size={{ xs: 12, md: 4 }} key={tier.name}>
                <Paper sx={{
                  p: 4, height: '100%',
                  background: tier.highlighted
                    ? `linear-gradient(135deg, ${service.color}18, ${service.color}08)`
                    : 'rgba(22,15,32,0.9)',
                  border: tier.highlighted
                    ? `2px solid ${service.color}88`
                    : '1px solid rgba(255,110,180,0.12)',
                  position: 'relative',
                  transition: 'all 0.3s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 12px 40px ${service.color}22` },
                }}>
                  {tier.highlighted && (
                    <Chip
                      label="Most Popular"
                      icon={<StarIcon sx={{ fontSize: '14px !important' }} />}
                      size="small"
                      sx={{
                        position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                        background: `linear-gradient(135deg, ${service.color}, ${service.color}cc)`,
                        color: '#fff', fontWeight: 700,
                      }}
                    />
                  )}
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>{tier.name}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>{tier.description}</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 4, color: service.color, fontSize: '2.2rem' }}>
                    {tier.price}
                  </Typography>
                  <Stack spacing={1.5} sx={{ mb: 4 }}>
                    {tier.features.map((f) => (
                      <Stack key={f} direction="row" spacing={1.5} alignItems="flex-start">
                        <CheckCircleOutlineIcon sx={{ color: service.color, fontSize: 18, mt: 0.15, flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{f}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                  <Button
                    fullWidth
                    component={RouterLink}
                    to={`/services/${service.id}/start/${tierIndex}`}
                    variant={tier.highlighted ? 'contained' : 'outlined'}
                    sx={tier.highlighted ? {
                      background: `linear-gradient(135deg, ${service.color}, ${service.color}cc)`,
                      '&:hover': { boxShadow: `0 0 20px ${service.color}44` },
                    } : {
                      borderColor: `${service.color}44`, color: service.color,
                      '&:hover': { borderColor: service.color, background: `${service.color}10` },
                    }}
                  >
                    Get Started
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* FAQ */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="overline" sx={{ color: service.color, letterSpacing: 4 }}>
            FAQ
          </Typography>
          <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, mt: 1, mb: 6 }}>
            Common Questions
          </Typography>
          <Stack spacing={2}>
            {service.faqs.map((faq, i) => (
              <Accordion
                key={i}
                expanded={expandedFaq === i}
                onChange={() => setExpandedFaq(expandedFaq === i ? false : i)}
                sx={{
                  background: 'rgba(22,15,32,0.9)',
                  border: `1px solid ${expandedFaq === i ? service.color + '55' : 'rgba(255,110,180,0.12)'}`,
                  borderRadius: '12px !important',
                  '&:before': { display: 'none' },
                  transition: 'border-color 0.3s',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: service.color }} />}
                  sx={{ px: 3 }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{faq.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>{faq.a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Box>

        {/* CTA */}
        <Paper sx={{
          p: { xs: 4, md: 6 }, textAlign: 'center',
          background: `linear-gradient(135deg, ${service.color}12, ${service.color}06)`,
          border: `1px solid ${service.color}33`,
        }}>
          <Typography variant="h4" sx={{ mb: 2 }}>Ready to get started?</Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, maxWidth: 480, mx: 'auto' }}>
            DM me on Discord — no commitment, just a chance to discuss your project.
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="https://discord.com/users/panekopanko"
            sx={{
              background: `linear-gradient(135deg, ${service.color}, ${service.color}cc)`,
              px: 5, py: 1.5,
              '&:hover': { boxShadow: `0 0 30px ${service.color}44` },
            }}
          >
            DM me on Discord
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}
